import React, { useEffect, useMemo, useState } from 'react';
import { ShieldCheck, FileCheck2, FileText, QrCode, Search, ChevronDown, ChevronUp, Users, Loader2 } from 'lucide-react';
import { fmtDate, portalApi, portalFileUrl } from './portalApi';
import { Card, Empty, Kpi, PageHeader, Pill, Spinner } from './portalUi';

const TicketList = ({ tickets }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
    {tickets.map((t) => (
      <div key={t.name} className={`rounded-lg border px-3 py-2 ${t.for_this_site ? 'border-blue-300 bg-blue-50/60' : 'border-slate-200 bg-white'}`} data-testid={`ticket-${t.name.replace(/\W+/g, '-').toLowerCase()}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="text-sm font-semibold text-slate-900 leading-tight">{t.name}{t.is_orientation && <span className="ml-1.5 text-[10px] font-bold text-blue-700 uppercase">orientation</span>}</div>
          <Pill tone="valid">valid</Pill>
        </div>
        <div className="text-xs text-slate-500 mt-1">{t.expiry_date ? `Valid to ${fmtDate(t.expiry_date)}` : 'No expiry'}{t.organization ? ` · ${t.organization}` : ''}</div>
      </div>))}
  </div>
);

const PdfLink = ({ r, status }) => {
  if (!r.employee_id) return null;
  if (r.pdf_available || status === 'ready') {
    return <a className="p-btn p-btn-ghost !py-1 !px-2.5 text-xs" href={portalFileUrl(`/safety/${r.employee_id}/tickets.pdf`)} target="_blank" rel="noreferrer" data-testid={`roster-pdf-${r.employee_id}`}><FileText className="w-3.5 h-3.5" /> Tickets PDF</a>;
  }
  if (status === 'queued' || status === 'generating') {
    return <span className="inline-flex items-center gap-1 text-xs text-slate-500" title="We're building this person's ticket PDF — it appears here automatically" data-testid={`roster-pdf-pending-${r.employee_id}`}><Loader2 className="w-3.5 h-3.5 animate-spin" /> Preparing PDF…</span>;
  }
  return <span className="text-xs text-slate-400" data-testid={`roster-pdf-none-${r.employee_id}`}>PDF not on file</span>;
};

const Row = ({ r, open, onToggle, pdfStatus }) => (
  <>
    <tr className="cursor-pointer" onClick={onToggle} data-testid={`roster-row-${r.employee_id || r.name}`}>
      <td className="font-semibold text-slate-900 whitespace-nowrap">{r.name}</td>
      <td>{r.trade}</td>
      <td className="whitespace-nowrap text-slate-600">{r.next_scheduled ? <span className="text-blue-700 font-semibold">Scheduled {fmtDate(r.next_scheduled)}</span> : r.last_on_site ? fmtDate(r.last_on_site) : '—'}</td>
      <td>{r.orientations.length === 0 ? <span className="text-slate-400 text-xs">none on file</span> : r.orientations.slice(0, 2).map((o) => <Pill key={o.name} tone="valid" className="mr-1 mb-1 !normal-case !tracking-normal">{o.name}</Pill>)}</td>
      <td>
        <div className="flex items-center gap-1 flex-wrap">
          {r.valid_count > 0 && <Pill tone="valid">{r.valid_count} valid</Pill>}
          {r.tickets.length === 0 && <span className="text-slate-400 text-xs">no tickets on file</span>}
        </div>
      </td>
      <td className="!text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
        {r.verify_token && <a className="inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-blue-700 mr-2" href={`/verify/${r.verify_token}`} target="_blank" rel="noreferrer" title="Live verification page (same as the hardhat QR sticker)" data-testid={`roster-qr-${r.employee_id}`}><QrCode className="w-3.5 h-3.5" /> Live</a>}
        <PdfLink r={r} status={pdfStatus} />
        <button type="button" className="ml-2 text-slate-400" aria-label="Expand">{open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}</button>
      </td>
    </tr>
    {open && <tr data-testid={`roster-detail-${r.employee_id || r.name}`}><td colSpan={6} className="!bg-slate-50">{r.tickets.length ? <TicketList tickets={r.tickets} /> : <span className="text-sm text-slate-500">No tickets recorded for this person yet — ask your PM and we will upload them.</span>}</td></tr>}
  </>
);

export default function PortalSafety() {
  const [data, setData] = useState(null);
  const [pdfStatus, setPdfStatus] = useState({});
  const [q, setQ] = useState('');
  const [ticket, setTicket] = useState('');
  const [open, setOpen] = useState({});
  useEffect(() => { portalApi.get('/safety').then((r) => { setData(r.data); setPdfStatus(Object.fromEntries(r.data.roster.map((x) => [x.employee_id || x.name, x.pdf_status]))); }); }, []);
  const pending = useMemo(() => Object.values(pdfStatus).some((s) => s === 'queued' || s === 'generating'), [pdfStatus]);
  useEffect(() => {
    if (!pending) return undefined;
    const t = setInterval(() => portalApi.get('/safety/pdf-status').then((r) => setPdfStatus(r.data)).catch(() => {}), 15000);
    return () => clearInterval(t);
  }, [pending]);
  const rows = useMemo(() => {
    if (!data) return [];
    const ql = q.trim().toLowerCase();
    return data.roster.filter((r) => (!ql || r.name.toLowerCase().includes(ql)) && (!ticket || r.tickets.some((t) => t.name === ticket)));
  }, [data, q, ticket]);
  if (!data) return <Spinner />;
  const s = data.summary;
  const ready = Object.values(pdfStatus).filter((x) => x === 'ready').length;
  const preparing = Object.values(pdfStatus).filter((x) => x === 'queued' || x === 'generating').length;

  return (
    <div data-testid="portal-safety">
      <PageHeader overline="Safety & site tickets" title="Who we send you, and their paper" subtitle="Everyone who worked or is scheduled on your work orders in the last 12 months, with their current trade tickets. Site orientations for your plant are highlighted."
        actions={<a className="p-btn p-btn-primary" href={portalFileUrl('/safety/package.pdf', true)} target="_blank" rel="noreferrer" data-testid="safety-package-btn"><FileCheck2 className="w-4 h-4" /> Export Crew Safety Package (PDF)</a>} />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-7">
        <Kpi testId="safety-kpi-crew" label="Crew on roster" icon={Users} tone="blue" value={s.crew} sub="Worked or scheduled" />
        <Kpi testId="safety-kpi-valid" label="Current tickets" icon={ShieldCheck} tone="emerald" value={s.valid_tickets} sub="Valid trade & safety tickets on file" badge={s.crew > 0 ? <Pill tone="valid">Audit ready</Pill> : null} />
        <Kpi testId="safety-kpi-pdfs" label="Ticket PDFs" icon={FileText} tone={preparing ? 'amber' : 'slate'} value={`${ready}/${s.crew}`} sub={preparing ? `${preparing} being prepared — refreshes automatically` : 'Ready to open'} />
        <Kpi testId="safety-kpi-orientation" label="Site orientations" tone={s.with_orientation ? 'blue' : 'slate'} value={`${s.with_orientation}/${s.crew}`} sub="Crew with an orientation on file" />
      </div>
      <Card>
        <div className="flex flex-wrap gap-2 px-5 pt-5 pb-3">
          <label className="relative block flex-1 min-w-[200px]"><Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" /><input className="p-input !pl-9" placeholder="Search by name" value={q} onChange={(e) => setQ(e.target.value)} data-testid="safety-search" /></label>
          <select className="p-input !w-auto max-w-[280px]" value={ticket} onChange={(e) => setTicket(e.target.value)} data-testid="safety-ticket-filter">
            <option value="">Any ticket</option>
            {data.ticket_types.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        {rows.length === 0 ? <Empty icon={ShieldCheck} title="Nobody matches" text="No crew has worked or been scheduled on your work orders yet." /> : (
          <div className="overflow-x-auto p-scroll"><table className="p-table" data-testid="roster-table">
            <thead><tr><th>Tradesperson</th><th>Trade</th><th>Last on site</th><th>Site orientation</th><th>Tickets</th><th></th></tr></thead>
            <tbody>{rows.map((r) => <Row key={r.employee_id || r.name} r={r} pdfStatus={pdfStatus[r.employee_id || r.name]} open={!!open[r.employee_id || r.name]} onToggle={() => setOpen((o) => ({ ...o, [r.employee_id || r.name]: !o[r.employee_id || r.name] }))} />)}</tbody>
          </table></div>)}
      </Card>
    </div>
  );
}
